import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Car, Plus, Gauge, Wrench, CalendarDays, Wallet } from "lucide-react";
import { api, km, dateFr, eur } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/vehicles"), api.get("/budget")])
      .then(([v, b]) => {
        setVehicles(v.data);
        setBudget(b.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const stats = [
    { label: "Véhicules", value: vehicles.length, icon: Car, to: "/app/vehicules" },
    { label: "Opérations", value: budget?.nb_operations ?? 0, icon: Wrench, to: "/app/entretien" },
    { label: "Dépenses totales", value: eur(budget?.total_depense), icon: Wallet, to: "/app/budget" },
    {
      label: "À faire",
      value: vehicles.reduce((s, v) => s + (v.a_faire || 0), 0),
      icon: CalendarDays,
      to: "/app/calendrier",
    },
  ];

  return (
    <div data-testid="dashboard-page" className="space-y-10">
      <div className="animate-fade-up">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Tableau de bord</p>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
          Bonjour {user?.name?.split(" ")[0] || ""}
        </h1>
        <p className="mt-2 text-base text-slate-600">
          Ne ratez plus jamais l'entretien de votre voiture.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s, i) => (
          <Link key={s.label} to={s.to} data-testid={`stat-${i}`}
            aria-label={s.label}
            className="block bg-white border border-slate-200 rounded-xl p-5 shadow-sm animate-fade-up hover:border-blue-300 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            style={{ animationDelay: `${i * 70}ms` }}>
            <s.icon className="h-5 w-5 text-blue-600" />
            <p className="mt-3 font-display text-2xl font-bold text-slate-900">{s.value}</p>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-1">{s.label}</p>
          </Link>
        ))}
      </div>

      <div>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-2xl font-bold text-slate-800">Mes véhicules</h2>
          <Link to="/app/vehicules?new=1" data-testid="dashboard-add-vehicle-btn"
            className="inline-flex items-center gap-2 rounded-full bg-blue-600 text-white px-5 py-2.5 text-sm font-medium hover:bg-blue-700 transition-colors">
            <Plus className="h-4 w-4" /> Ajouter
          </Link>
        </div>

        {loading ? (
          <p className="text-slate-500">Chargement…</p>
        ) : vehicles.length === 0 ? (
          <div data-testid="dashboard-empty" className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center">
            <Car className="h-8 w-8 text-slate-400 mx-auto" />
            <p className="mt-4 font-display text-lg font-semibold text-slate-800">Aucun véhicule</p>
            <p className="mt-1 text-sm text-slate-500">Commencez par ajouter votre voiture.</p>
            <Link to="/app/vehicules?new=1" data-testid="empty-add-vehicle-btn"
              className="mt-6 inline-flex rounded-full bg-blue-600 text-white px-6 py-2.5 text-sm font-medium hover:bg-blue-700 transition-colors">
              Ajouter mon véhicule
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
            {vehicles.map((v, i) => (
              <div key={v.id} data-testid={`dashboard-vehicle-card-${v.id}`}
                className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm animate-fade-up hover:-translate-y-0.5 transition-transform"
                style={{ animationDelay: `${i * 70}ms` }}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-display text-xl font-semibold text-slate-900">
                      {v.marque} {v.modele}
                    </h3>
                    <p className="text-sm text-slate-500">
                      {v.immatriculation ? `${v.immatriculation} · ` : ""}{v.annee} · {v.carburant || "—"}
                    </p>
                  </div>
                  <StatusBadge status={v.statut} testId={`vehicle-status-${v.id}`} />
                </div>

                <div className="mt-5 flex items-center gap-2 text-slate-700">
                  <Gauge className="h-4 w-4 text-slate-400" />
                  <span className="font-display font-semibold">{km(v.kilometrage)}</span>
                </div>

                <div className="mt-4 pt-4 border-t border-slate-100 space-y-3 text-sm">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Dernier entretien</p>
                    <p className="mt-1 text-slate-700">
                      {v.dernier_entretien
                        ? `${v.dernier_entretien.type_entretien} — ${dateFr(v.dernier_entretien.date)}`
                        : "Aucun enregistrement"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Prochains entretiens</p>
                    {v.prochaines_echeances?.length ? (
                      <ul className="mt-1 space-y-1.5">
                        {v.prochaines_echeances.slice(0, 3).map((e) => (
                          <li key={e.id} className="flex items-center justify-between gap-2">
                            <span className="text-slate-700 truncate">{e.type_entretien}</span>
                            <StatusBadge status={e.statut}
                              label={e.due_date ? dateFr(e.due_date) : km(e.due_km)} />
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="mt-1 text-slate-500">Aucune échéance saisie</p>
                    )}
                  </div>
                </div>

                <Link to={`/app/vehicules/${v.id}`} data-testid={`vehicle-etat-${v.id}`}
                  className="mt-5 inline-flex text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors mr-4">
                  État de mon entretien →
                </Link>
                <Link to={`/app/entretien?vehicle=${v.id}`} data-testid={`vehicle-open-${v.id}`}
                  className="mt-5 inline-flex text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors">
                  Voir le carnet →
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
